import { useEffect, useState, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import type { ActiveBot, BotConfig, PublicMessage } from '../types';
import {
  subscribeBotConfig, updateBotConfig,
  subscribeActiveBots, deleteActiveBot, deleteAllActiveBots,
  deleteExpiredBots,
  getBotStats, type BotStats, getBotConversationLog, resetBotStats,
} from '../services/botService';
import { useAuthStore } from '../stores/authStore';
import UserDetailModal from '../components/common/UserDetailModal';

// ─── Sub-components ───

function Toggle({ checked, onChange, label }: { checked: boolean; onChange: (v: boolean) => void; label: string }) {
  return (
    <label className="flex items-center gap-2 cursor-pointer">
      <button
        type="button"
        onClick={() => onChange(!checked)}
        className={`relative w-10 h-5 rounded-full transition ${checked ? 'bg-blue-600' : 'bg-gray-300'}`}
      >
        <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform ${checked ? 'translate-x-5' : ''}`} />
      </button>
      <span className="text-sm text-gray-700">{label}</span>
    </label>
  );
}

function NumberInput({ value, onChange, label, min, max, step }: {
  value: number; onChange: (v: number) => void; label: string; min?: number; max?: number; step?: number;
}) {
  const [text, setText] = useState(String(value));
  useEffect(() => { setText(String(value)); }, [value]);
  return (
    <label className="block">
      <span className="text-sm text-gray-600">{label}</span>
      <input
        type="text"
        inputMode="decimal"
        value={text}
        onChange={(e) => {
          const raw = e.target.value;
          setText(raw);
          const n = Number(raw);
          if (raw !== '' && !isNaN(n)) onChange(n);
        }}
        onBlur={() => {
          const n = Number(text);
          if (isNaN(n) || text === '') { setText(String(value)); return; }
          const clamped = Math.max(min ?? -Infinity, Math.min(max ?? Infinity, n));
          onChange(clamped);
          setText(String(clamped));
        }}
        className="mt-1 block w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
      />
    </label>
  );
}

// ─── Main Page ───

type Tab = 'settings' | 'monitor' | 'prompts' | 'profiles';

export default function BotManagerPage() {
  const location = useLocation();
  const { adminUser } = useAuthStore();
  const initTab = (location.state as any)?.tab as Tab | undefined;
  const [tab, setTab] = useState<Tab>(initTab || 'monitor');
  const [config, setConfig] = useState<BotConfig | null>(null);
  const [activeBots, setActiveBots] = useState<ActiveBot[]>([]);
  const [stats, setStats] = useState<BotStats | null>(null);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [message, setMessage] = useState('');

  // Subscribe to config and active bots
  useEffect(() => {
    const unsubs = [
      subscribeBotConfig((cfg) => { setConfig(cfg); setDirty(false); }),
      subscribeActiveBots(setActiveBots),
    ];
    getBotStats().then(setStats);
    // Refresh stats every 30 seconds
    const statsInterval = setInterval(() => getBotStats().then(setStats), 30_000);
    return () => { unsubs.forEach((u) => u()); clearInterval(statsInterval); };
  }, []);

  // Update local config
  function updateField<K extends keyof BotConfig>(key: K, value: BotConfig[K]) {
    if (!config) return;
    setConfig({ ...config, [key]: value });
    setDirty(true);
  }

  // Toggle enabled with immediate save + confirmation
  async function handleToggleEnabled() {
    if (!config) return;
    const next = !config.enabled;
    const msg = next
      ? '봇 시스템을 활성화하시겠습니까?'
      : '봇 시스템을 비활성화하시겠습니까?';
    if (!confirm(msg)) return;
    try {
      await updateBotConfig({ enabled: next });
      setMessage(next ? '봇 시스템이 활성화되었습니다.' : '봇 시스템이 비활성화되었습니다.');
    } catch (err) {
      setMessage('오류: ' + (err as Error).message);
    }
    setTimeout(() => setMessage(''), 3000);
  }

  // Cost-sensitive toggle: persist instantly so user doesn't need to click 저장
  async function handleToggleGeocoding() {
    if (!config) return;
    const next = !config.useGeocodingAPI;
    try {
      await updateBotConfig({ useGeocodingAPI: next });
      setMessage(next
        ? 'Geocoding API 호출이 켜졌습니다. 봇 생성마다 과금됩니다.'
        : 'Geocoding API 호출이 꺼졌습니다.');
    } catch (err) {
      setMessage('오류: ' + (err as Error).message);
    }
    setTimeout(() => setMessage(''), 3000);
  }

  // Save config
  async function handleSave() {
    if (!config || !dirty) return;
    setSaving(true);
    try {
      await updateBotConfig(config);
      setMessage('설정이 저장되었습니다.');
      setDirty(false);
    } catch (err) {
      setMessage('저장 실패: ' + (err as Error).message);
    } finally {
      setSaving(false);
      setTimeout(() => setMessage(''), 3000);
    }
  }

  // Reset the monitoring counters (message count + cost estimate)
  async function handleResetStats() {
    await resetBotStats(adminUser?.email ?? adminUser?.uid ?? 'unknown');
    setStats(await getBotStats());
    setMessage('봇 메시지/비용 카운터를 초기화했습니다.');
    setTimeout(() => setMessage(''), 3000);
  }

  // Emergency stop
  async function handleEmergencyStop() {
    if (!confirm('모든 봇을 즉시 비활성화하고 삭제하시겠습니까?')) return;
    try {
      await updateBotConfig({ enabled: false });
      await deleteAllActiveBots();
      setMessage('모든 봇이 비활성화되었습니다.');
    } catch (err) {
      setMessage('오류: ' + (err as Error).message);
    }
    setTimeout(() => setMessage(''), 3000);
  }

  if (!config) {
    return <div className="text-gray-500">Loading...</div>;
  }

  const tabs: { key: Tab; label: string }[] = [
    { key: 'monitor', label: '모니터링' },
    { key: 'settings', label: '봇 설정' },
    { key: 'profiles', label: '봇 프로필' },
    { key: 'prompts', label: '프롬프트' },
  ];

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">AI 봇 관리</h1>
          <p className="text-sm text-gray-500 mt-1">
            활성 봇: <span className="font-semibold text-blue-600">{activeBots.length}</span>
            {stats && <> | 총 봇 메시지: <span className="font-semibold">{stats.totalBotMessages}</span></>}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleEmergencyStop}
            className="px-3 py-1.5 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 transition"
          >
            긴급 중지
          </button>
          {dirty && (
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-4 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition"
            >
              {saving ? '저장 중...' : '저장'}
            </button>
          )}
        </div>
      </div>

      {/* Status message */}
      {message && (
        <div className={`mb-4 px-4 py-2 rounded-lg text-sm ${message.includes('실패') || message.includes('오류') ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`}>
          {message}
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 mb-4 bg-gray-100 rounded-lg p-1">
        {tabs.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`flex-1 px-3 py-2 text-sm rounded-md transition ${tab === t.key ? 'bg-white text-gray-900 shadow-sm font-medium' : 'text-gray-500 hover:text-gray-700'}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {tab === 'settings' && <SettingsTab config={config} updateField={updateField} onToggleEnabled={handleToggleEnabled} onToggleGeocoding={handleToggleGeocoding} />}
      {tab === 'monitor' && <MonitorTab bots={activeBots} stats={stats} config={config} onDeleteBot={deleteActiveBot} onResetStats={handleResetStats} />}
      {tab === 'profiles' && <ProfilesTab config={config} updateField={updateField} />}
      {tab === 'prompts' && <PromptsTab config={config} updateField={updateField} />}
    </div>
  );
}

// ─── Settings Tab ───

function SettingsTab({ config, updateField, onToggleEnabled, onToggleGeocoding }: {
  config: BotConfig;
  updateField: <K extends keyof BotConfig>(key: K, value: BotConfig[K]) => void;
  onToggleEnabled: () => void;
  onToggleGeocoding: () => void;
}) {
  return (
    <div className="space-y-6">
      {/* Master Toggle */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <h2 className="text-lg font-semibold text-gray-900 mb-3">기본 설정</h2>
        <div className="space-y-3">
          <Toggle checked={config.enabled} onChange={onToggleEnabled} label="봇 활성화" />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <label className="block">
              <span className="text-sm text-gray-600">GPT 모델</span>
              <select
                value={config.gptModel}
                onChange={(e) => updateField('gptModel', e.target.value)}
                className="mt-1 block w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg"
              >
                <option value="gpt-4o-mini">gpt-4o-mini</option>
                <option value="gpt-4o">gpt-4o</option>
                <option value="gpt-4.1-mini">gpt-4.1-mini</option>
                <option value="gpt-4.1-nano">gpt-4.1-nano</option>
              </select>
            </label>
            <NumberInput value={config.maxTokens} onChange={(v) => updateField('maxTokens', v)} label="Max Tokens" min={50} max={500} />
            <NumberInput value={config.temperature} onChange={(v) => updateField('temperature', v)} label="Temperature" min={0} max={2} step={0.1} />
          </div>
        </div>
      </div>

      {/* API Keys */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <h2 className="text-lg font-semibold text-gray-900 mb-3">API 키</h2>
        <div className="space-y-3">
          <label className="block">
            <span className="text-sm text-gray-600">OpenAI API Key</span>
            <input
              type="password"
              value={config.openaiApiKey}
              onChange={(e) => updateField('openaiApiKey', e.target.value)}
              placeholder="sk-..."
              className="mt-1 block w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg font-mono"
            />
            <p className="text-xs text-gray-400 mt-1">환경변수(OPENAI_API_KEY) 설정 시 여기 입력 불필요</p>
          </label>
          <label className="block">
            <span className="text-sm text-gray-600">Google Maps API Key</span>
            <input
              type="password"
              value={config.googleMapsApiKey}
              onChange={(e) => updateField('googleMapsApiKey', e.target.value)}
              placeholder="AIza..."
              className="mt-1 block w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg font-mono"
            />
            <p className="text-xs text-gray-400 mt-1">봇 생성 시 정확한 위치(동/구) 파악용. 없으면 오프라인 DB로 대도시 단위 파악</p>
          </label>
          <div className="flex items-start justify-between gap-4 pt-1 border-t border-gray-100">
            <div className="flex-1">
              <span className="text-sm font-medium text-gray-700">Geocoding 호출로 정확한 지역 정보 사용</span>
              <p className="text-xs text-gray-500 mt-0.5">
                켜면 봇 생성 시마다 Google Geocoding API 호출 ($5/1K). 끄면 오프라인 도시 DB만 사용 (무료, 대도시 단위).
                앰비언트/사용자 메시지/봇 메시지 트리거 모든 경로에 즉시 적용됩니다.
              </p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={config.useGeocodingAPI}
              onClick={onToggleGeocoding}
              className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${
                config.useGeocodingAPI ? 'bg-blue-600' : 'bg-gray-300'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  config.useGeocodingAPI ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
        </div>
      </div>

      {/* Response Probabilities */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <h2 className="text-lg font-semibold text-gray-900 mb-3">응답 확률</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <NumberInput value={config.userMessageResponseProbability} onChange={(v) => updateField('userMessageResponseProbability', v)} label="사용자 메시지 응답 확률" min={0} max={1} step={0.05} />
          <NumberInput value={config.botConversationProbability} onChange={(v) => updateField('botConversationProbability', v)} label="봇 대화 참여 확률" min={0} max={1} step={0.05} />
          <NumberInput value={config.additionalResponseProbability} onChange={(v) => updateField('additionalResponseProbability', v)} label="추가 응답 확률" min={0} max={1} step={0.05} />
          <NumberInput value={config.botChatEmojiProbability} onChange={(v) => updateField('botChatEmojiProbability', v)} label="대화 이모지 확률" min={0} max={1} step={0.05} />
          <NumberInput value={config.goodbyeProbability} onChange={(v) => updateField('goodbyeProbability', v)} label="작별 인사 확률" min={0} max={1} step={0.05} />
        </div>
      </div>

      {/* Bot Lifetime */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <h2 className="text-lg font-semibold text-gray-900 mb-3">봇 수명</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <NumberInput value={config.activityDurationMin} onChange={(v) => updateField('activityDurationMin', v)} label="최소 활동 시간 (분)" min={1} />
          <NumberInput value={config.activityDurationMax} onChange={(v) => updateField('activityDurationMax', v)} label="최대 활동 시간 (분)" min={1} />
          <NumberInput value={config.maxBotsPerArea} onChange={(v) => updateField('maxBotsPerArea', v)} label="반경 내 최대 봇 수" min={1} max={50} />
          <NumberInput value={config.botLimitRadiusKm} onChange={(v) => updateField('botLimitRadiusKm', v)} label="밀도 체크 반경 (km)" min={0.1} max={10} step={0.1} />
          <NumberInput value={config.maxResponsesPerUser} onChange={(v) => updateField('maxResponsesPerUser', v)} label="사용자당 최대 응답 수" min={1} max={10} />
          <NumberInput value={config.minBotResponseInterval} onChange={(v) => updateField('minBotResponseInterval', v)} label="봇 최소 응답 간격 (초)" min={1} />
        </div>
      </div>

      {/* Runaway Prevention */}
      <div className="bg-white rounded-xl shadow-sm border border-red-200 p-4">
        <h2 className="text-lg font-semibold text-red-700 mb-1">폭주 방지</h2>
        <p className="text-xs text-gray-500 mb-3">봇끼리 무한 대화를 방지하는 안전장치</p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <NumberInput value={config.maxConversationChainDepth} onChange={(v) => updateField('maxConversationChainDepth', v)} label="최대 체인 깊이" min={1} max={20} />
          <NumberInput value={config.maxBotMessagesPerWindow} onChange={(v) => updateField('maxBotMessagesPerWindow', v)} label="시간창 내 최대 봇 메시지" min={1} max={100} />
          <NumberInput value={config.botMessageWindowSeconds} onChange={(v) => updateField('botMessageWindowSeconds', v)} label="시간창 (초)" min={10} max={600} />
        </div>
      </div>

      {/* Response Settings */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <h2 className="text-lg font-semibold text-gray-900 mb-3">응답 설정</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <NumberInput value={config.responseDelayMin} onChange={(v) => updateField('responseDelayMin', v)} label="최소 응답 지연 (초)" min={1} />
          <NumberInput value={config.responseDelayMax} onChange={(v) => updateField('responseDelayMax', v)} label="최대 응답 지연 (초)" min={1} />
          <NumberInput value={config.responseDistanceMin} onChange={(v) => updateField('responseDistanceMin', v)} label="최소 응답 거리 (m)" min={10} />
          <NumberInput value={config.responseDistanceMax} onChange={(v) => updateField('responseDistanceMax', v)} label="최대 응답 거리 (m)" min={10} />
        </div>
      </div>

      {/* Conversation Context */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <h2 className="text-lg font-semibold text-gray-900 mb-3">대화 컨텍스트</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <NumberInput value={config.conversationHistoryCount} onChange={(v) => updateField('conversationHistoryCount', v)} label="대화 히스토리 개수" min={1} max={20} />
          <NumberInput value={config.conversationHistoryDistance} onChange={(v) => updateField('conversationHistoryDistance', v)} label="대화 히스토리 거리 (m)" min={100} max={10000} />
        </div>
      </div>

      {/* Auto Conversation */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <h2 className="text-lg font-semibold text-gray-900 mb-3">자동 대화</h2>
        <div className="space-y-3">
          <Toggle checked={config.autoConversationEnabled} onChange={(v) => updateField('autoConversationEnabled', v)} label="자동 대화 활성화" />
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <NumberInput value={config.autoConversationIntervalMin} onChange={(v) => updateField('autoConversationIntervalMin', v)} label="최소 간격 (초)" min={10} />
            <NumberInput value={config.autoConversationIntervalMax} onChange={(v) => updateField('autoConversationIntervalMax', v)} label="최대 간격 (초)" min={10} />
            <NumberInput value={config.autoConversationProbability} onChange={(v) => updateField('autoConversationProbability', v)} label="자동 대화 확률" min={0} max={1} step={0.05} />
          </div>
        </div>
      </div>

      {/* Ambient Bots */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <h2 className="text-lg font-semibold text-gray-900 mb-1">앰비언트 봇</h2>
        <p className="text-xs text-gray-500 mb-3">도심지 주변에 자동 생성되어 레이더/지도에 표시. 먼저 말하지 않고 응답만 함</p>
        <div className="space-y-3">
          <Toggle checked={config.ambientBotsEnabled} onChange={(v) => updateField('ambientBotsEnabled', v)} label="앰비언트 봇 활성화" />
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <NumberInput value={config.ambientBotsMax} onChange={(v) => updateField('ambientBotsMax', v)} label="최대 앰비언트 봇 수" min={1} max={50} />
            <NumberInput value={config.ambientBotsPerCycle} onChange={(v) => updateField('ambientBotsPerCycle', v)} label="사이클당 생성 수" min={1} max={10} />
            <NumberInput value={config.ambientSpawnIntervalMinutes} onChange={(v) => updateField('ambientSpawnIntervalMinutes', v)} label="생성 주기 (분)" min={10} max={1440} step={5} />
          </div>
          <p className="text-xs text-gray-500">생성 주기마다 최대 봇 수에 도달할 때까지 사이클당 생성 수만큼 추가됩니다. 스케줄러가 10분마다 실행되므로 10분 미만은 10분과 동일하게 동작합니다.</p>
        </div>
      </div>

      {/* Bot DM Reply */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <h2 className="text-lg font-semibold text-gray-900 mb-1">봇 쪽지 자동 응답</h2>
        <p className="text-xs text-gray-500 mb-3">봇이 쪽지를 받으면 자동으로 읽고 답장</p>
        <div className="space-y-3">
          <Toggle checked={config.botDmReplyEnabled} onChange={(v) => updateField('botDmReplyEnabled', v)} label="쪽지 자동 응답 활성화" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <NumberInput value={config.botDmReplyDelayMin} onChange={(v) => updateField('botDmReplyDelayMin', v)} label="최소 응답 지연 (초)" min={5} />
            <NumberInput value={config.botDmReplyDelayMax} onChange={(v) => updateField('botDmReplyDelayMax', v)} label="최대 응답 지연 (초)" min={5} />
          </div>
        </div>
      </div>

      {/* Cleanup */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <h2 className="text-lg font-semibold text-gray-900 mb-3">정리</h2>
        <NumberInput value={config.cleanupBotMessagesAfterDays} onChange={(v) => updateField('cleanupBotMessagesAfterDays', v)} label="봇 메시지 삭제 (일, 0=삭제 안함)" min={0} />
      </div>
    </div>
  );
}

// ─── Bot Conversation Log Modal ───

function BotLogModal({ bot, onClose }: { bot: ActiveBot; onClose: () => void }) {
  const [messages, setMessages] = useState<PublicMessage[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getBotConversationLog(bot).then((msgs) => { setMessages(msgs); setLoading(false); });
  }, [bot.uid]);

  function fmtTime(ts: { toDate?: () => Date }) {
    if (!ts?.toDate) return '';
    return ts.toDate().toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-gray-900">{bot.nickname} 대화 로그</h3>
            <p className="text-xs text-gray-400">{bot.country} | {bot.persona.personality.type} | 메시지 {bot.messageCount}개</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-lg">&times;</button>
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-2">
          {loading ? (
            <div className="text-center text-gray-400 py-8">로딩 중...</div>
          ) : messages.length === 0 ? (
            <div className="text-center text-gray-400 py-8">대화 기록이 없습니다.</div>
          ) : messages.map((msg) => {
            const isBot = msg.senderId === bot.uid;
            return (
              <div key={msg.id} className={`flex ${isBot ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[80%] px-3 py-2 rounded-lg text-sm ${isBot ? 'bg-blue-100 text-blue-900' : 'bg-gray-100 text-gray-800'}`}>
                  <div className="flex items-center gap-1.5 mb-0.5">
                    <span className={`text-xs font-medium ${isBot ? 'text-blue-600' : 'text-gray-500'}`}>
                      {isBot ? `${bot.nickname} (봇)` : msg.senderNickname}
                    </span>
                    <span className="text-xs text-gray-400">{fmtTime(msg.timestamp)}</span>
                  </div>
                  <div>{msg.text}</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ─── Monitor Tab ───

type BotSortKey = 'nickname' | 'gender' | 'spawnType' | 'personality' | 'language' | 'country' | 'messageCount' | 'createdAt' | 'expiresAt';

function MonitorTab({ bots, stats, config, onDeleteBot, onResetStats }: {
  bots: ActiveBot[];
  stats: BotStats | null;
  config: BotConfig;
  onDeleteBot: (uid: string) => Promise<void>;
  onResetStats: () => Promise<void>;
}) {
  const [deleting, setDeleting] = useState<string | null>(null);
  const [resetting, setResetting] = useState(false);
  const [cleaningExpired, setCleaningExpired] = useState(false);
  const [userDetailUid, setUserDetailUid] = useState<string | null>(null);
  const [logBot, setLogBot] = useState<ActiveBot | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortKey, setSortKey] = useState<BotSortKey>(() => (localStorage.getItem('bot_monitor_sortKey') as BotSortKey) || 'createdAt');
  const [sortAsc, setSortAsc] = useState(() => localStorage.getItem('bot_monitor_sortAsc') === 'true');

  function handleSort(key: BotSortKey) {
    if (sortKey === key) {
      const next = !sortAsc;
      setSortAsc(next);
      localStorage.setItem('bot_monitor_sortAsc', String(next));
    } else {
      setSortKey(key);
      setSortAsc(false);
      localStorage.setItem('bot_monitor_sortKey', key);
      localStorage.setItem('bot_monitor_sortAsc', 'false');
    }
  }

  function tsMs(ts: any): number {
    return ts?.toDate?.()?.getTime?.() ?? (ts?.seconds ?? 0) * 1000;
  }

  const sortedBots = useMemo(() => {
    let filtered = bots;
    if (searchQuery) {
      const kw = searchQuery.toLowerCase();
      filtered = bots.filter(b =>
        b.nickname?.toLowerCase().includes(kw) ||
        b.uid?.toLowerCase().includes(kw) ||
        b.language?.toLowerCase().includes(kw) ||
        b.country?.toLowerCase().includes(kw) ||
        (b.spawnType || 'user').includes(kw) ||
        b.persona?.personality?.type?.toLowerCase().includes(kw)
      );
    }
    const sorted = [...filtered].sort((a, b) => {
      let cmp = 0;
      switch (sortKey) {
        case 'nickname': cmp = (a.nickname || '').localeCompare(b.nickname || ''); break;
        case 'gender': cmp = (a.gender || '').localeCompare(b.gender || ''); break;
        case 'spawnType': cmp = (a.spawnType || 'user').localeCompare(b.spawnType || 'user'); break;
        case 'personality': cmp = (a.persona?.personality?.type || '').localeCompare(b.persona?.personality?.type || ''); break;
        case 'language': cmp = (a.language || '').localeCompare(b.language || ''); break;
        case 'country': cmp = (a.country || '').localeCompare(b.country || ''); break;
        case 'messageCount': cmp = (a.messageCount || 0) - (b.messageCount || 0); break;
        case 'createdAt': cmp = tsMs(a.createdAt) - tsMs(b.createdAt); break;
        case 'expiresAt': cmp = tsMs(a.expiresAt) - tsMs(b.expiresAt); break;
      }
      return sortAsc ? cmp : -cmp;
    });
    return sorted;
  }, [bots, searchQuery, sortKey, sortAsc]);

  async function handleDelete(uid: string, nickname: string) {
    if (!confirm(`"${nickname}" 봇을 삭제하시겠습니까?`)) return;
    setDeleting(uid);
    try { await onDeleteBot(uid); }
    finally { setDeleting(null); }
  }

  async function handleDeleteExpired() {
    const expiredCount = bots.filter(b => {
      const d = b.expiresAt?.toDate ? b.expiresAt.toDate() : new Date((b.expiresAt as any)?.seconds * 1000);
      return d.getTime() <= Date.now();
    }).length;
    if (expiredCount === 0) { alert('만료된 봇이 없습니다.'); return; }
    if (!confirm(`만료된 봇 ${expiredCount}개를 삭제하시겠습니까?`)) return;
    setCleaningExpired(true);
    try {
      const deleted = await deleteExpiredBots();
      alert(`${deleted}개의 만료 봇을 삭제했습니다.`);
    } finally { setCleaningExpired(false); }
  }

  async function handleResetStats() {
    if (!confirm('봇 메시지 수와 예상 비용을 0부터 다시 집계합니다.\n(메시지 자체는 삭제되지 않습니다.)\n\n계속하시겠습니까?')) return;
    setResetting(true);
    try { await onResetStats(); }
    catch (err) { alert('초기화 실패: ' + (err as Error).message); }
    finally { setResetting(false); }
  }

  function formatTime(ts: { toDate?: () => Date; seconds?: number }) {
    if (!ts) return '-';
    const d = ts.toDate ? ts.toDate() : new Date((ts.seconds ?? 0) * 1000);
    return d.toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  }

  function timeRemaining(ts: { toDate?: () => Date; seconds?: number }): string {
    if (!ts) return '-';
    const d = ts.toDate ? ts.toDate() : new Date((ts.seconds ?? 0) * 1000);
    const diff = d.getTime() - Date.now();
    if (diff <= 0) return '만료됨';
    const min = Math.floor(diff / 60000);
    return `${min}분 남음`;
  }

  // Token cost estimation
  // gpt-4o-mini: ~$0.15/1M input + $0.60/1M output tokens
  // Average per bot message: ~300 input tokens (system prompt + context) + ~50 output tokens
  const totalBotMessages = stats?.totalBotMessages ?? 0;
  const avgInputPerMsg = 300;
  const avgOutputPerMsg = 50;
  const estInputTokens = totalBotMessages * avgInputPerMsg;
  const estOutputTokens = totalBotMessages * avgOutputPerMsg;
  const modelName = config.gptModel ?? 'gpt-4o-mini';
  const isExpensiveModel = modelName.includes('gpt-4o') && !modelName.includes('mini');
  const inputRate = isExpensiveModel ? 2.5 : 0.15;  // per 1M tokens
  const outputRate = isExpensiveModel ? 10.0 : 0.60;
  const estCost = (estInputTokens * inputRate + estOutputTokens * outputRate) / 1_000_000;

  const resetAtLabel = stats?.resetAt
    ? stats.resetAt.toDate().toLocaleString('ko-KR', { dateStyle: 'short', timeStyle: 'short' })
    : null;

  const costCard = (
    <>
    <div className="mb-2 flex items-center justify-between gap-3">
      <span className="text-xs text-gray-500">
        {resetAtLabel
          ? `${resetAtLabel} 초기화 이후 집계 (전체 누적 ${(stats?.totalBotMessagesRaw ?? 0).toLocaleString()}개)`
          : '전체 누적 집계'}
      </span>
      <button
        onClick={handleResetStats}
        disabled={resetting}
        className="px-3 py-1 text-xs border border-gray-300 text-gray-600 rounded hover:bg-gray-50 disabled:opacity-50 whitespace-nowrap"
      >
        {resetting ? '초기화 중...' : '메시지/비용 초기화'}
      </button>
    </div>
    <div className="mb-3 grid grid-cols-2 sm:grid-cols-4 gap-3">
      <div className="bg-white rounded-lg border border-gray-200 p-3 text-center">
        <div className="text-xs text-gray-500">활성 봇</div>
        <div className="text-xl font-bold text-blue-600">{bots.length}</div>
      </div>
      <div className="bg-white rounded-lg border border-gray-200 p-3 text-center">
        <div className="text-xs text-gray-500">총 봇 메시지</div>
        <div className="text-xl font-bold text-purple-600">{totalBotMessages.toLocaleString()}</div>
      </div>
      <div className="bg-white rounded-lg border border-gray-200 p-3 text-center">
        <div className="text-xs text-gray-500">예상 토큰 사용</div>
        <div className="text-xl font-bold text-orange-600">{((estInputTokens + estOutputTokens) / 1000).toFixed(0)}K</div>
        <div className="text-[10px] text-gray-400">in {(estInputTokens / 1000).toFixed(0)}K + out {(estOutputTokens / 1000).toFixed(0)}K</div>
      </div>
      <div className="bg-white rounded-lg border border-gray-200 p-3 text-center">
        <div className="text-xs text-gray-500">예상 비용 ({modelName})</div>
        <div className="text-xl font-bold text-green-600">${estCost.toFixed(4)}</div>
        <div className="text-[10px] text-gray-400">${inputRate}/1M in + ${outputRate}/1M out</div>
      </div>
    </div>
    </>
  );

  if (bots.length === 0) {
    return (
      <>
        {costCard}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center text-gray-500">
          현재 활동 중인 봇이 없습니다.
        </div>
      </>
    );
  }

  const expiredCount = bots.filter(b => {
    const d = b.expiresAt?.toDate ? b.expiresAt.toDate() : new Date((b.expiresAt as any)?.seconds * 1000);
    return d.getTime() <= Date.now();
  }).length;

  return (
    <>
      {costCard}
      <div className="mb-3 flex items-center gap-3">
        <input
          type="text"
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          placeholder="닉네임, 언어, 국가, 유형, 성격 검색..."
          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-blue-500"
        />
        <span className="text-sm text-gray-500 whitespace-nowrap">
          {searchQuery ? `${sortedBots.length} / ${bots.length}` : `${bots.length}개`}
        </span>
      </div>
      {expiredCount > 0 && (
        <div className="mb-3 flex items-center justify-between px-4 py-2 bg-red-50 border border-red-200 rounded-lg">
          <span className="text-sm text-red-700">만료된 봇 <strong>{expiredCount}</strong>개가 있습니다. (5분 주기 자동 삭제)</span>
          <button
            onClick={handleDeleteExpired}
            disabled={cleaningExpired}
            className="px-3 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
          >
            {cleaningExpired ? '삭제 중...' : '만료 봇 즉시 삭제'}
          </button>
        </div>
      )}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                {([
                  ['nickname', '닉네임', 'text-left', ''],
                  ['gender' as BotSortKey, '성별', 'text-center', 'hidden sm:table-cell'],
                  ['spawnType', '유형', 'text-left', 'hidden sm:table-cell'],
                  ['personality', '성격', 'text-left', 'hidden sm:table-cell'],
                  ['language', '언어', 'text-left', 'hidden md:table-cell'],
                  ['country', '국가', 'text-left', 'hidden lg:table-cell'],
                  ['messageCount', '메시지', 'text-center', ''],
                  ['createdAt', '생성', 'text-left', 'hidden sm:table-cell'],
                  ['expiresAt', '남은 시간', 'text-left', ''],
                ] as [BotSortKey, string, string, string][]).map(([key, label, align, hide]) => (
                  <th key={key} className={`${align} px-4 py-3 font-medium text-gray-600 ${hide}`}>
                    <button onClick={() => handleSort(key)} className="hover:text-blue-600 transition">
                      {label} {sortKey === key ? (sortAsc ? '▲' : '▼') : ''}
                    </button>
                  </th>
                ))}
                <th className="text-center px-4 py-3 font-medium text-gray-600">삭제</th>
              </tr>
            </thead>
            <tbody>
              {sortedBots.map((bot) => (
                <tr key={bot.uid} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="px-4 py-2.5">
                    <button
                      onClick={() => setUserDetailUid(bot.uid)}
                      className="text-left hover:underline flex items-center gap-2"
                    >
                      <div className="w-8 h-8 rounded-full overflow-hidden border border-gray-200 shrink-0 flex items-center justify-center bg-gray-100">
                        {bot.profileImageUrl ? (
                          <img src={bot.profileImageUrl} className="w-full h-full object-cover" />
                        ) : (
                          <span className="text-xs">{bot.gender === 'male' ? '👨' : bot.gender === 'female' ? '👩' : '🧑'}</span>
                        )}
                      </div>
                      <div>
                        <div className="font-medium text-blue-600">{bot.nickname}</div>
                        <div className="text-xs text-gray-400">{bot.uid.slice(0, 8)}...</div>
                      </div>
                    </button>
                  </td>
                  <td className="px-4 py-2.5 text-center">
                    <span className="text-sm">{bot.gender === 'male' ? '♂' : bot.gender === 'female' ? '♀' : '?'}</span>
                  </td>
                  <td className="px-4 py-2.5">
                    <span className={`inline-block px-2 py-0.5 text-xs rounded-full ${
                      bot.spawnType === 'ambient' ? 'bg-teal-100 text-teal-700'
                      : bot.spawnType === 'bot' ? 'bg-orange-100 text-orange-700'
                      : 'bg-blue-100 text-blue-700'
                    }`}>
                      {bot.spawnType === 'ambient' ? '앰비언트' : bot.spawnType === 'bot' ? '봇 응답' : '사용자 응답'}
                    </span>
                  </td>
                  <td className="px-4 py-2.5">
                    <span className="inline-block px-2 py-0.5 text-xs rounded-full bg-purple-100 text-purple-700">
                      {bot.persona?.personality?.type ?? '-'}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-gray-600">{bot.language}</td>
                  <td className="px-4 py-2.5 text-gray-600">{bot.country}</td>
                  <td className="px-4 py-2.5 text-center">
                    <button
                      onClick={() => setLogBot(bot)}
                      className="text-blue-600 hover:text-blue-800 hover:underline font-medium"
                    >
                      {bot.messageCount}
                    </button>
                  </td>
                  <td className="px-4 py-2.5 text-gray-500 text-xs">{formatTime(bot.createdAt)}</td>
                  <td className="px-4 py-2.5">
                    <span className={`text-xs font-medium ${timeRemaining(bot.expiresAt) === '만료됨' ? 'text-red-500' : 'text-green-600'}`}>
                      {timeRemaining(bot.expiresAt)}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-center">
                    <button
                      onClick={() => handleDelete(bot.uid, bot.nickname)}
                      disabled={deleting === bot.uid}
                      className="text-red-500 hover:text-red-700 disabled:opacity-50 text-xs"
                    >
                      {deleting === bot.uid ? '...' : '삭제'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* User Detail Modal */}
      {userDetailUid && (
        <UserDetailModal uid={userDetailUid} onClose={() => setUserDetailUid(null)} />
      )}

      {/* Bot Conversation Log Modal */}
      {logBot && (
        <BotLogModal bot={logBot} onClose={() => setLogBot(null)} />
      )}
    </>
  );
}

// ─── Profiles Tab ───

function ProfilesTab({ config, updateField }: {
  config: BotConfig;
  updateField: <K extends keyof BotConfig>(key: K, value: BotConfig[K]) => void;
}) {
  type PGender = 'male' | 'female' | 'unknown';
  const GENDERS: { key: PGender; label: string; color: string }[] = [
    { key: 'male', label: '남성', color: 'border-blue-500 text-blue-600' },
    { key: 'female', label: '여성', color: 'border-pink-500 text-pink-600' },
    { key: 'unknown', label: '비공개', color: 'border-gray-500 text-gray-600' },
  ];

  const [genderTab, setGenderTab] = useState<PGender>('male');
  const [images, setImages] = useState<Record<PGender, { name: string; url: string; fullPath: string }[]>>({ male: [], female: [], unknown: [] });
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [deleting, setDeleting] = useState(false);
  const cacheRef = useRef<Record<string, { name: string; url: string; fullPath: string }[]> | null>(null);

  useEffect(() => { loadAll(); }, []);

  async function loadAll(forceRefresh = false) {
    if (!forceRefresh && cacheRef.current) {
      setImages(cacheRef.current as any);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const { listBotProfileImagesByGender } = await import('../services/botProfileService');
      const [m, f, u] = await Promise.all([
        listBotProfileImagesByGender('male'),
        listBotProfileImagesByGender('female'),
        listBotProfileImagesByGender('unknown'),
      ]);
      const data = { male: m, female: f, unknown: u };
      setImages(data);
      cacheRef.current = data as any;
    } finally { setLoading(false); }
  }

  async function handleUpload(files: FileList | null) {
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      const { uploadBotProfileImage, cropImageToSquare } = await import('../services/botProfileService');
      for (const file of Array.from(files)) {
        const cropped = await cropImageToSquare(file);
        const name = `bot_${Date.now()}_${Math.random().toString(36).slice(2, 8)}.png`;
        await uploadBotProfileImage(cropped, name, genderTab);
      }
      await loadAll(true);
    } catch (err) {
      alert('업로드 실패: ' + (err as Error).message);
    } finally { setUploading(false); }
  }

  async function handleDeleteSelected() {
    if (selected.size === 0) return;
    if (!confirm(`선택한 ${selected.size}개 이미지를 삭제하시겠습니까?`)) return;
    setDeleting(true);
    try {
      const { deleteBotProfileImage } = await import('../services/botProfileService');
      for (const fp of selected) {
        await deleteBotProfileImage(fp);
      }
      setSelected(new Set());
      await loadAll(true);
    } finally { setDeleting(false); }
  }

  function toggleSelect(fullPath: string) {
    const s = new Set(selected);
    s.has(fullPath) ? s.delete(fullPath) : s.add(fullPath);
    setSelected(s);
  }

  function selectAllCurrent() {
    const all = currentImages.map(i => i.fullPath);
    const allSelected = all.every(fp => selected.has(fp));
    const s = new Set(selected);
    if (allSelected) { all.forEach(fp => s.delete(fp)); }
    else { all.forEach(fp => s.add(fp)); }
    setSelected(s);
  }

  const currentImages = images[genderTab];
  const totalCount = images.male.length + images.female.length + images.unknown.length;
  const currentSelected = currentImages.filter(i => selected.has(i.fullPath)).length;

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">봇 프로필 이미지</h2>
            <p className="text-xs text-gray-500">봇 생성 시 성별에 맞는 프로필 이미지 랜덤 할당 (총 {totalCount}장)</p>
          </div>
          <div className="flex gap-2">
            {selected.size > 0 && (
              <button onClick={handleDeleteSelected} disabled={deleting}
                className="px-3 py-1.5 text-sm rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50">
                {deleting ? '삭제 중...' : `${selected.size}개 삭제`}
              </button>
            )}
            <label className={`px-3 py-1.5 text-sm rounded-lg cursor-pointer ${uploading ? 'bg-gray-300' : 'bg-blue-600 text-white hover:bg-blue-700'}`}>
              {uploading ? '업로드 중...' : '이미지 추가'}
              <input type="file" accept="image/*" multiple onChange={e => handleUpload(e.target.files)} className="hidden" disabled={uploading} />
            </label>
          </div>
        </div>

        {/* Gender tabs */}
        <div className="flex items-center gap-1 mb-3 border-b border-gray-200">
          {GENDERS.map(g => (
            <button
              key={g.key}
              onClick={() => setGenderTab(g.key)}
              className={`px-4 py-2 text-sm font-medium border-b-2 ${genderTab === g.key ? g.color : 'border-transparent text-gray-400'}`}
            >
              {g.label} ({images[g.key].length})
            </button>
          ))}
          {currentImages.length > 0 && (
            <button onClick={selectAllCurrent} className="ml-auto text-xs text-gray-500 hover:text-blue-600 px-2">
              {currentSelected === currentImages.length ? '선택 해제' : '전체 선택'}
            </button>
          )}
        </div>

        {loading ? (
          <div className="text-center text-gray-400 py-8">로딩 중...</div>
        ) : currentImages.length === 0 ? (
          <div className="text-center text-gray-400 py-8">
            {GENDERS.find(g => g.key === genderTab)?.label} 프로필 이미지가 없습니다. 이미지를 추가해주세요.
          </div>
        ) : (
          <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 lg:grid-cols-10 gap-2">
            {currentImages.map(img => (
              <div key={img.fullPath} className="relative group cursor-pointer" onClick={() => toggleSelect(img.fullPath)}>
                <img src={img.url} alt={img.name}
                  className={`w-full aspect-square rounded-lg object-cover border-2 ${selected.has(img.fullPath) ? 'border-blue-500 opacity-80' : 'border-gray-200'}`}
                  loading="lazy"
                />
                {selected.has(img.fullPath) && (
                  <div className="absolute top-1 left-1 w-5 h-5 rounded-full bg-blue-600 text-white flex items-center justify-center text-xs">
                    ✓
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Profile Settings */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <h2 className="text-lg font-semibold text-gray-900 mb-1">프로필 설정</h2>
        <p className="text-xs text-gray-500 mb-3">봇 생성 시 프로필 이미지와 자기소개 자동 할당 확률</p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <NumberInput value={config.botProfileImageProbability} onChange={(v) => updateField('botProfileImageProbability', v)} label="프로필 이미지 확률" min={0} max={1} step={0.05} />
          <NumberInput value={config.botBioProbability} onChange={(v) => updateField('botBioProbability', v)} label="자기소개 생성 확률" min={0} max={1} step={0.05} />
          <NumberInput value={config.botBioEmojiProbability} onChange={(v) => updateField('botBioEmojiProbability', v)} label="자기소개 이모지 확률" min={0} max={1} step={0.05} />
        </div>
      </div>
    </div>
  );
}

// ─── Default Prompts (mirrors Cloud Functions buildSystemPrompt) ───

const DEFAULT_PROMPTS = {
  system: `너는 랜덤 채팅앱의 실제 사용자야. 위치: {location}.
닉네임: {nickname}. {ageGroup}. 성별: {gender}.

[성격]
봇 생성 시 자동으로 결정됨 (active/quiet/playful/serious/emotional 중 하나)
- 말투(반말/존댓말), 이모지 사용량, 말의 길이 등이 성격에 따라 다름
- 질문할 수도, 리액션만 할 수도 있음 - 자연스럽게 대화

[핵심 규칙]
- 성격과 말투를 일관되게 유지
- 자연스러운 사람처럼 대화 (공감, 자기 이야기, 의견)
- 한 번에 1~3문장 정도
- AI 절대 인정 금지. 도움 제안 금지

※ 영어/일본어 사용자에게는 해당 언어 버전 자동 적용
※ 봇의 페르소나(성격, 관심사, 말투 습관)가 프롬프트에 자동 포함됨`,

  goodbye: `봇의 성격에 맞는 자연스러운 작별 인사 1문장.
예: "나 밥먹으러 갈게ㅋ" "슬슬 가봐야겠다~" "ㅂㅂ 다음에 또 보자"`,

  additionalResponse: `이전 말에 이어서 자연스럽게 한마디 더.
봇의 성격과 대화 맥락에 맞게 생성됨.`,

  autoConversation: `혼잣말이나 주변에 말 거는 메시지 1개. 자연스럽게.
예: "오늘 날씨 진짜 좋다" "누구 심심한 사람?" "배고프다 뭐 먹지"`,
};

// ─── Prompts Tab ───

function PromptSection({ title, description, value, defaultPrompt, onChange }: {
  title: string;
  description?: string;
  value: string;
  defaultPrompt: string;
  onChange: (v: string) => void;
}) {
  const [showDefault, setShowDefault] = useState(false);

  function handleCopyDefault() {
    onChange(defaultPrompt);
    setShowDefault(false);
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
        <button
          type="button"
          onClick={() => setShowDefault(!showDefault)}
          className="px-2.5 py-1 text-xs bg-gray-100 text-gray-600 rounded-md hover:bg-gray-200 transition"
        >
          {showDefault ? '닫기' : '기본 프롬프트 보기'}
        </button>
      </div>
      {description && <p className="text-xs text-gray-400 mb-2">{description}</p>}

      {showDefault && (
        <div className="mb-3 rounded-lg border border-blue-200 bg-blue-50 p-3">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-medium text-blue-700">기본 프롬프트 (코드에 내장됨)</span>
            <button
              type="button"
              onClick={handleCopyDefault}
              className="px-2 py-0.5 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 transition"
            >
              복사하여 편집하기
            </button>
          </div>
          <pre className="text-xs text-blue-900 whitespace-pre-wrap font-mono leading-relaxed max-h-80 overflow-y-auto">
            {defaultPrompt}
          </pre>
        </div>
      )}

      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={value ? 10 : 4}
        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg font-mono resize-y focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
        placeholder="비워두면 기본 프롬프트가 사용됩니다..."
      />
      {value && (
        <button
          type="button"
          onClick={() => onChange('')}
          className="mt-1 text-xs text-gray-400 hover:text-red-500 transition"
        >
          기본 프롬프트로 되돌리기
        </button>
      )}
    </div>
  );
}

function PromptsTab({ config, updateField }: {
  config: BotConfig;
  updateField: <K extends keyof BotConfig>(key: K, value: BotConfig[K]) => void;
}) {
  return (
    <div className="space-y-6">
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg px-4 py-3 text-sm text-yellow-800">
        비워두면 코드에 내장된 기본 프롬프트가 사용됩니다. 커스텀 프롬프트를 입력하면 기본 프롬프트를 <strong>완전히 대체</strong>합니다.
        &quot;기본 프롬프트 보기&quot; 버튼으로 현재 기본값을 확인하고 복사하여 수정할 수 있습니다.
      </div>

      <PromptSection
        title="시스템 프롬프트 템플릿"
        description="봇의 핵심 성격/행동을 정의합니다. 변수: {nickname}, {ageGroup}, {gender}, {personality}, {city}, {region}, {characteristics}, {localTerms}, {languageStyle} 등"
        value={config.systemPromptTemplate}
        defaultPrompt={DEFAULT_PROMPTS.system}
        onChange={(v) => updateField('systemPromptTemplate', v)}
      />

      <PromptSection
        title="작별 인사 프롬프트"
        value={config.goodbyePromptTemplate}
        defaultPrompt={DEFAULT_PROMPTS.goodbye}
        onChange={(v) => updateField('goodbyePromptTemplate', v)}
      />

      <PromptSection
        title="추가 응답 프롬프트"
        value={config.additionalResponsePromptTemplate}
        defaultPrompt={DEFAULT_PROMPTS.additionalResponse}
        onChange={(v) => updateField('additionalResponsePromptTemplate', v)}
      />

      <PromptSection
        title="자동 대화 프롬프트"
        value={config.autoConversationPromptTemplate}
        defaultPrompt={DEFAULT_PROMPTS.autoConversation}
        onChange={(v) => updateField('autoConversationPromptTemplate', v)}
      />
    </div>
  );
}
